import type { ReactElement } from "react"
import { useCallback } from "react"
import OverlayContainerOrg, {
  headerTypes,
  sizes,
  startPositions,
} from "@onestore/hel/dist/components/organisms/OverlayContainerOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import SurfaceOrg from "@onestore/hel/dist/components/organisms/SurfaceOrg"
import OnColorContext from "@onestore/hel/dist/contexts/OnColorContext"
import _t from "@core/i18n"
import useDomainSearchAiFooterModalContentQuery from "@gatsby-plugin-domain-search/hooks/useDomainSearchAiFooterModalContentQuery"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import MarkdownTitle from "@gatsby-plugin-generic-page/components/Markdown/Title"
import getBaseMarkdownOverrides from "@gatsby-plugin-generic-page/helpers/getBaseMarkdownOverrides"
import { filledGenerativeWrite } from "@ionos-web-design-system/icon/system"
import { DomainSearchBar, Link, Text } from "@ionos-web-design-system/react"
import UdsThemeProvider from "~/components/UdsThemeProvider"
import isEmpty from "~/lib/isEmpty"
import {
  hideAIPromptFooterModal,
  showAIPromptFooterModal,
} from "~/store/domain-search/actions"
import { getIsOpenAIPromptFooterModal } from "~/store/domain-search/selectors"
import { useAppDispatch, useAppSelector } from "~/store/hooks"

type FooterWithModalProps = {
  modalContent: string
}

function FooterWithModal({
  modalContent,
}: FooterWithModalProps): ReactElement<FooterWithModalProps> {
  const dispatch = useAppDispatch()
  const isOpen = useAppSelector(getIsOpenAIPromptFooterModal)

  const handleOpen = useCallback((): void => {
    dispatch(showAIPromptFooterModal())
  }, [dispatch])

  const handleClose = useCallback((): void => {
    dispatch(hideAIPromptFooterModal())
  }, [dispatch])

  const udsText = (
    <UdsThemeProvider>
      <PushOrg leftUnit={1}>
        <Text variant="bodySm" color="subtle">
          {_t("domainSearch.promptFooterTextBeforeLink")}{" "}
          <Link
            onClick={handleOpen}
            concept="mono"
            title={_t("domainSearch.promptFooterLink")}
            size="small"
          >
            {_t("domainSearch.promptFooterLink")}
          </Link>{" "}
          {_t("domainSearch.promptFooterTextAfterLink")}
        </Text>
      </PushOrg>
    </UdsThemeProvider>
  )

  return (
    <>
      {udsText}

      {isOpen ? (
        <OnColorContext.Provider value="backgroundLight">
          <OverlayContainerOrg
            size={sizes.MEDIUM}
            startPosition={startPositions.RIGHT}
            headerType={headerTypes.SHADOWED}
            backgroundColor="background"
            header={
              <MarkdownTitle
                components={getBaseMarkdownOverrides({
                  typography: "large2",
                })}
              >
                {_t("domainSearch.promptFooterModalTitle")}
              </MarkdownTitle>
            }
            headerWidth="default"
            onCloseButtonClick={handleClose}
          >
            <SurfaceOrg color="background" spacingSpace={0}>
              <MarkdownText hasOpenedLinksInNewTab>{modalContent}</MarkdownText>
            </SurfaceOrg>
          </OverlayContainerOrg>
        </OnColorContext.Provider>
      ) : null}
    </>
  )
}

type DomainSearchPromptProps = {
  value: string
  isPending: boolean
  setValue: { (value: string): void }
  onSubmitButtonClick: { (): void }
  onRandomizePrompt: { (): void }
}

function DomainSearchPrompt({
  value,
  isPending,
  setValue,
  onSubmitButtonClick,
  onRandomizePrompt,
}: DomainSearchPromptProps): ReactElement<DomainSearchPromptProps> {
  const { footerModalContent } = useDomainSearchAiFooterModalContentQuery()

  return (
    <>
      <DomainSearchBar
        variant="multi-line"
        value={value}
        placeholder={_t("domainSearch.ai.placeholder")}
        loading={isPending}
        onChange={setValue}
        buttons={[
          {
            text: _t("domainSearch.ai.promptSubmitButton"),
            variant: "primary",
            concept: "ai",
            onClick: onSubmitButtonClick,
            testId: "ai_search_button",
          },
          {
            text: _t("domainSearch.promptRandomButton"),
            variant: "secondary",
            concept: "ai",
            icon: filledGenerativeWrite,
            onClick: onRandomizePrompt,
            testId: "sample_prompt_button",
          },
        ]}
        responsiveButton
      />

      {!isEmpty(footerModalContent) ? (
        <PushOrg topSpace={1} hasNoReset>
          <FooterWithModal modalContent={footerModalContent} />
        </PushOrg>
      ) : null}
    </>
  )
}

export default DomainSearchPrompt
